import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Company, CompanyGateway, InvoiceItem } from '../types';
import { NotaGridContainer } from '../cardStyles';
import { rotuloNota } from '../constants';
import RemoveInvoiceItemModal from '../../../../../components/Modal/RemoveInvoiceItemModal';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import AddInvoiceItemsModal from '../AddInvoiceItemsModal';
import TotaisSection from '../Sections/TotaisSection';
import FreteSection from '../Sections/FreteSection';
import InfoAdicionaisSection from '../Sections/InfoAdicionaisSection';
import PagamentoSection from '../Sections/PagamentoSection';
import DestinatarioSection from '../Sections/DestinatarioSection';
import IdentificacaoSection, { IdentificacaoValues } from '../Sections/IdentificacaoSection';
import ItensSection from '../Sections/ItensSection';
import { useItemTaxResolution } from '../useItemTaxResolution';
import { icmsComFrete } from '../icmsComFrete';
import { useCenarioFiscalDosItens } from '../useCenarioFiscalDosItens';
import { useEmissionPolling } from '../useEmissionPolling';
import InvoiceHeaderActions from '../InvoiceHeaderActions';
import { AvisoAcao, AvisoPedidoAlterado, ColunaLateral, NegadaAlert, NegadaIcone, TextoAviso } from '../style';

interface DivergenciaPedido {
  itemId: number;
  descricao: string;
  tipo: 'ITEM_SEM_VINCULO' | 'NCM' | 'PRECO';
  noPedido?: string | null;
  naNota?: string | null;
}

const textoDivergencia = (d: DivergenciaPedido): string => {
  const noPedido = d.noPedido ?? 'sem valor';
  const naNota = d.naNota ?? 'sem valor';
  switch (d.tipo) {
    case 'NCM':
      return `${d.descricao}: NCM no pedido ${noPedido} · na nota ${naNota}`;
    case 'PRECO':
      return `${d.descricao}: preço no pedido ${noPedido} · na nota ${naNota}`;
    case 'ITEM_SEM_VINCULO':
      return `${d.descricao}: item não está mais no pedido`;
  }
};

interface SnapshotNota {
  ident: IdentificacaoValues;
  itens: Map<number, { cfop: string; ncmId: InvoiceItem['ncmId'] }>;
}

const IDENT_CAMPOS = ['companyEmissorId', 'naturezaOperacao', 'tipoOperacao', 'finalidade'] as const;

const InvoiceForm = () => {
  const { id: invoiceId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const { setHeaderActions } = useOutletContext<{ setHeaderActions?: (acoes: React.ReactNode) => void }>() ?? {};
  const [companies, setCompanies] = useState<Company[]>([]);
  const [emitindo, setEmitindo] = useState(false);
  const [transportadoras, setTransportadoras] = useState<Company[]>([]);
  const [emissoras, setEmissoras] = useState<CompanyGateway[] | null>(null);
  const [selectedDestinatario, setSelectedDestinatario] = useState<{ company: Company } | null>(null);
  const [cfops, setCfops] = useState<{ id: number; code: string; description: string }[]>([]);
  const [cfopsPerItem, setCfopsPerItem] = useState<Map<number, { id: number; code: string; description: string }[]>>(new Map());
  const [ncmOptions, setNcmOptions] = useState<{ id: number; ncm: string }[]>([]);

  const [invoice, setInvoice] = useState<any>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  // NEGADA também é editável (mesma janela de RASCUNHO) — sem isso a nota rejeitada
  // trava em "já emitida" e o usuário vê o motivo sem conseguir corrigir.
  const isEmitida = !!invoice?.status && !['RASCUNHO', 'NEGADA'].includes(invoice.status);
  const valorFreteNota = Number(invoice?.valorFreteNota ?? 0) || 0;
  // Nota emitida mostra o snapshot; rascunho recalcula base e ICMS com o frete rateado.
  const itensExibidos = useMemo(
    () => (isEmitida ? items : icmsComFrete(items, valorFreteNota)),
    [isEmitida, items, valorFreteNota],
  );
  const podeEditarItens = invoice?.status === 'RASCUNHO' || invoice?.status === 'NEGADA';
  const [removeItemTarget, setRemoveItemTarget] = useState<{ id: number; descricao: string; quantidade: number } | null>(null);
  const [showAddItems, setShowAddItems] = useState(false);
  const [showCancelNfe, setShowCancelNfe] = useState(false);
  const [cancelandoNfe, setCancelandoNfe] = useState(false);

  const [identValues, setIdentValues] = useState<IdentificacaoValues>({
    companyEmissorId: '',
    naturezaOperacao: '',
    tipoOperacao: '',
    finalidade: '',
  });

  const [snapshot, setSnapshot] = useState<SnapshotNota | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmaSaida, setConfirmaSaida] = useState(false);
  const [carga, setCarga] = useState(0);
  const cargaComBaseline = useRef(0);
  const usuarioEditou = useRef(false);
  const [confirmaRefazer, setConfirmaRefazer] = useState(false);
  const [refazendo, setRefazendo] = useState(false);

  const formatBrlNumber = (val: any) =>
    val ? Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const response = await ApiService.fiscal.getInvoice(Number(invoiceId));
      const data = response.data;
      if (!data) return;

      setInvoice(data);
      usuarioEditou.current = false;
      const identCarregada: IdentificacaoValues = {
        companyEmissorId: data.companyEmissorId ? String(data.companyEmissorId) : '',
        naturezaOperacao: data.naturezaOperacao || '',
        tipoOperacao: data.tipoOperacao || '',
        finalidade: data.finalidade || '',
      };
      setIdentValues(identCarregada);
      setSnapshot({ ident: identCarregada, itens: new Map() });
      if (data.CompanyCliente) {
        setSelectedDestinatario({ company: data.CompanyCliente });
      }
      if (Array.isArray(data.InvoiceProduct)) {
        const mapped = data.InvoiceProduct.map((p: any) => ({
          id: p.id || p.orderItemId,
          orderItemId: p.orderItemId ?? null,
          codigo: p.codigoProduto || `ITEM-${p.orderItemId || p.id}`,
          descricao: p.descricaoProduto || 'Produto Sem Descrição',
          ncmId: p.ncmId || null,
          cfopId: null,
          cfop: p.cfop || '',
          ncmCode: p.Ncm?.ncm || '',
          cst: p.cstICMS || '',
          cstIpi: p.cstIPI || '',
          cEnqIpi: p.codigoEnquadramentoIPI || '',
          cstPisCofins: p.cstPIS_COFINS || '',
          cstIbsCbs: p.cstIBS_CBS || '',
          classificacao: p.classificacaoTributaria || '',
          // Item sem unidade própria (aço) não persiste unitOfMeasurementId — o fallback
          // espelha o da emissão, que emite 'UN'.
          unidade: p.UnitOfMeasurement?.name || 'un',
          quantidade: p.quantidade || 0,
          vlUnit: formatBrlNumber(p.valorUnitario) || '0,00',
          vlTotal: formatBrlNumber(p.valorTotal) || '0,00',
          bcIcms: formatBrlNumber(p.baseCalculoICMS || p.valorTotal),
          vlIcms: formatBrlNumber(p.valorICMS),
          vlIpi: formatBrlNumber(p.valorIPI),
          vlIbs: formatBrlNumber(p.valorIBS),
          vlCbs: formatBrlNumber(p.valorCBS),
          aliqIcms: p.aliquotaICMS ?? '',
          aliqIpi: p.aliquotaIPI ?? '',
          _lastLookupHash: '',
        }));
        setItems(mapped);
        setSnapshot({
          ident: identCarregada,
          itens: new Map(mapped.map((i: InvoiceItem) => [i.id, { cfop: i.cfop, ncmId: i.ncmId }])),
        });
      }
      setCarga((atual) => atual + 1);
    } catch (error) {
      console.error('Erro ao buscar detalhes da nota fiscal:', error);
      setFeedback({ type: 'error', message: 'Erro ao carregar detalhes da nota fiscal.', isOpen: true, statusOk: false });
    }
  }, [invoiceId, setFeedback]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Sincroniza com o Nota Gateway (consultarStatus grava EMITIDA/NEGADA) e então relê.
  // Só reler (loadInvoice) deixaria a nota presa em EMITINDO — o status não muda sozinho.
  const syncStatus = useCallback(async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.nfeStatus(Number(invoiceId));
    } catch (error) {
      console.error('Erro ao consultar status da NF-e:', error);
    }
    await loadInvoice();
  }, [invoiceId, loadInvoice]);

  useEmissionPolling({ isEmitindo: invoice?.status === 'EMITINDO', onTick: syncStatus });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await ApiService.companies.findAll();
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setCompanies(data || []);
      } catch (error) {
        console.error('Erro ao buscar empresas:', error);
      }
    };
    fetchCompanies();

    // lista própria: o destinatário escolhe entre todas as empresas, a transportadora não
    const fetchTransportadoras = async () => {
      try {
        const response = await ApiService.companies.findAll({
          companyType: 'Transportadora',
          excludeMainCompany: true,
        });
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setTransportadoras(data || []);
      } catch (error) {
        console.error('Erro ao buscar transportadoras:', error);
      }
    };
    fetchTransportadoras();

    const fetchEmissoras = async () => {
      try {
        const response = await ApiService.fiscal.getCompanyGateways('nota');
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setEmissoras(data || []);
      } catch (error) {
        console.error('Erro ao buscar empresas emissoras:', error);
      }
    };
    fetchEmissoras();

    const fetchCfops = async () => {
      try {
        const response = await ApiService.fiscal.getCfops();
        const data = Array.isArray(response.data) ? response.data : response.data.data;
        setCfops(data || []);
      } catch (error) {
        console.error('Erro ao buscar CFOPs:', error);
      }
    };
    fetchCfops();
  }, []);

  const destStateId = (() => {
    const c = selectedDestinatario?.company as any;
    return (
      c?.stateId ||
      c?.state?.id ||
      c?.Address?.Neighborhood?.City?.stateId ||
      c?.address?.neighborhood?.city?.stateId ||
      null
    );
  })();

  // Emissor selecionado — o cenário fiscal (CFOP/NCM/lookup) depende dele.
  const companyEmissorId = identValues.companyEmissorId ? Number(identValues.companyEmissorId) : null;

  // A lista só traz emissores operacionais: um rascunho com emissor pendente ou inativo
  // precisa trocar de emissor antes de emitir (o backend recusaria com 422).
  const emissorIndisponivel =
    !isEmitida && emissoras !== null && companyEmissorId !== null && !emissoras.some((cg) => cg.companyId === companyEmissorId);

  // Trocar a emissora invalida os caches de CFOP/NCM e força novo lookup: as opções
  // e a tributação são por emissor. Sem isso, ficariam presas no emissor anterior.
  useEffect(() => {
    setCfopsPerItem(new Map());
    setNcmOptions([]);
    setItems((prev) => prev.map((p) => ({ ...p, _lastLookupHash: '' })));
  }, [companyEmissorId]);

  useItemTaxResolution({ items, setItems, cfops, setCfopsPerItem, destStateId, isEmitida, companyEmissorId });

  const sugerirNaturezaOperacao = useCallback(
    (naturezaOperacao: string) => setIdentValues((prev) => ({ ...prev, naturezaOperacao })),
    [],
  );
  const cenario = useCenarioFiscalDosItens({
    invoiceId: invoiceId ? Number(invoiceId) : null,
    pedidoTaxConfigId: invoice?.Order?.taxConfigId ?? null,
    items,
    setItems,
    cfops,
    destStateId,
    companyEmissorId,
    isEmitida,
    naturezaOperacaoAtual: identValues.naturezaOperacao,
    onSugerirNaturezaOperacao: sugerirNaturezaOperacao,
  });

  const itensEstabilizados = items.every((i) => {
    if (!i.ncmId || !destStateId || !companyEmissorId) return true;
    if (i.cfopId) return !!i._lastLookupHash;
    const opcoes = cfopsPerItem.get(i.id);
    return opcoes !== undefined && opcoes.length !== 1;
  });
  const resolucaoConcluida =
    emissoras !== null && cfops.length > 0 && cenario.pronto && itensEstabilizados;

  useEffect(() => {
    if (carga === 0 || cargaComBaseline.current === carga || !resolucaoConcluida) return;
    cargaComBaseline.current = carga;
    if (usuarioEditou.current) return;
    setSnapshot({
      ident: identValues,
      itens: new Map(items.map((i) => [i.id, { cfop: i.cfop, ncmId: i.ncmId }])),
    });
  }, [carga, resolucaoConcluida, identValues, items]);

  const setItemsPeloUsuario: typeof setItems = (proximo) => {
    usuarioEditou.current = true;
    setItems(proximo);
  };

  const fetchNcmsForItem = async (item: InvoiceItem) => {
    if (!item.cfopId || !destStateId || !companyEmissorId) { setNcmOptions([]); return; }
    try {
      const response = await ApiService.fiscal.getNcms({ cfopId: item.cfopId, stateId: destStateId, companyEmissorId });
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setNcmOptions(data || []);
    } catch {
      setNcmOptions([]);
    }
  };

  const montarPayload = () => ({
    naturezaOperacao: identValues.naturezaOperacao || undefined,
    finalidade: identValues.finalidade || undefined,
    tipoOperacao: identValues.tipoOperacao || undefined,
    companyEmissorId: identValues.companyEmissorId ? Number(identValues.companyEmissorId) : undefined,
    items: items.map((i) => ({ itemId: Number(i.id), cfop: i.cfop || undefined, ncmId: i.ncmId || undefined })),
  });

  const editavel = invoice?.status === 'RASCUNHO' || invoice?.status === 'NEGADA';
  const alterado =
    editavel &&
    snapshot !== null &&
    (IDENT_CAMPOS.some((campo) => identValues[campo] !== snapshot.ident[campo]) ||
      items.some((i) => {
        const original = snapshot.itens.get(i.id);
        return !!original && (original.cfop !== i.cfop || original.ncmId !== i.ncmId);
      }));

  const handleSalvar = async () => {
    if (!invoiceId || salvando || emitindo) return;
    setSalvando(true);
    try {
      await ApiService.fiscal.updateInvoice(Number(invoiceId), montarPayload());
      await loadInvoice();
      setFeedback({ message: 'Alterações salvas.', isOpen: true, statusOk: true });
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({ message: Array.isArray(msg) ? msg.join('; ') : (msg || 'Erro ao salvar as alterações.'), isOpen: true, statusOk: false });
    } finally {
      setSalvando(false);
    }
  };

  const handleDescartar = () => {
    if (!snapshot) return;
    setIdentValues(snapshot.ident);
    setItems((prev) =>
      prev.map((i) => {
        const original = snapshot.itens.get(i.id);
        return original ? { ...i, cfop: original.cfop, ncmId: original.ncmId } : i;
      }),
    );
  };

  const handleEmitir = async () => {
    if (!invoiceId || emitindo) return;

    if (!identValues.companyEmissorId) {
      setFeedback({ message: 'Selecione a empresa emissora antes de emitir.', isOpen: true, statusOk: false });
      return;
    }
    if (emissorIndisponivel) {
      setFeedback({ message: 'Empresa emissora pendente/inativa — escolha outra para emitir.', isOpen: true, statusOk: false });
      return;
    }
    if (!identValues.naturezaOperacao) {
      setFeedback({ message: 'Selecione a natureza da operação antes de emitir.', isOpen: true, statusOk: false });
      return;
    }

    const itensSemCfop = items.filter((i) => !i.cfop);
    // Lista vazia = o NCM não tem natureza para este emissor e UF; escolher CFOP não resolve.
    const semCenario = itensSemCfop.filter((i) => cfopsPerItem.get(i.id)?.length === 0);
    if (semCenario.length > 0) {
      const ncms = semCenario.map((i) => `${i.ncmCode || 'sem código'} (${i.descricao})`).join(', ');
      setFeedback({ message: `Sem cenário fiscal neste emissor e UF para o NCM ${ncms}. Cadastre em Cenário Fiscal.`, isOpen: true, statusOk: false });
      return;
    }
    if (itensSemCfop.length > 0) {
      setFeedback({ message: `CFOP não informado para: ${itensSemCfop.map((i) => i.descricao).join(', ')}`, isOpen: true, statusOk: false });
      return;
    }
    const itensSemNcm = items.filter((i) => !i.ncmId);
    if (itensSemNcm.length > 0) {
      setFeedback({ message: `NCM não informado para: ${itensSemNcm.map((i) => i.descricao).join(', ')}`, isOpen: true, statusOk: false });
      return;
    }

    setEmitindo(true);
    try {
      await ApiService.fiscal.updateInvoice(Number(invoiceId), montarPayload());
      await ApiService.fiscal.emitir(Number(invoiceId));
      setFeedback({ message: 'Emissão iniciada. Aguardando autorização da SEFAZ...', isOpen: true, statusOk: true });
      loadInvoice();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({
        message: Array.isArray(msg) ? msg.join('; ') : (msg || 'Erro ao emitir a nota.'),
        isOpen: true,
        statusOk: false,
        semTitulo: error?.response?.status === 422,
      });
    } finally {
      setEmitindo(false);
    }
  };

  const orderIdRascunho: number | null = invoice?.orderId ?? null;
  const divergenciasPedido: DivergenciaPedido[] = Array.isArray(invoice?.divergenciasPedido) ? invoice.divergenciasPedido : [];
  const rascunhoDiverge = editavel && invoice?.rascunhoDivergeDoPedido === true && orderIdRascunho !== null;

  const handleRefazer = async () => {
    if (!invoiceId || orderIdRascunho === null || refazendo) return;
    setRefazendo(true);
    try {
      await ApiService.fiscal.deleteInvoice(Number(invoiceId));
      navigate(`/panel/steel?faturar=${orderIdRascunho}`, { replace: true });
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao excluir o rascunho.', isOpen: true, statusOk: false });
      setConfirmaRefazer(false);
    } finally {
      setRefazendo(false);
    }
  };

  const handleCancelar = async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.cancelar(Number(invoiceId));
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      loadInvoice();
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    }
  };

  const confirmarCancelamentoNfe = async () => {
    setCancelandoNfe(true);
    try {
      await handleCancelar();
    } finally {
      setCancelandoNfe(false);
      setShowCancelNfe(false);
    }
  };

  // Handlers via ref: nos deps do effect eles recriariam o nó a cada render e o setHeaderActions entraria em loop.
  const voltar = () => (alterado ? setConfirmaSaida(true) : navigate(-1));
  const acoesHeader = useRef({ emitir: handleEmitir, salvar: handleSalvar, descartar: handleDescartar, voltar });
  acoesHeader.current = { emitir: handleEmitir, salvar: handleSalvar, descartar: handleDescartar, voltar };
  const statusNota: string | undefined = invoice?.status;
  const linkDanfe: string | undefined = invoice?.linkDanfe ?? undefined;

  useEffect(() => {
    setHeaderActions?.(
      <InvoiceHeaderActions
        mode="edit"
        status={statusNota}
        podeEmitir={!!invoiceId && !emissorIndisponivel && !emitindo && !salvando}
        alterado={alterado}
        salvando={salvando}
        onVoltar={() => acoesHeader.current.voltar()}
        onEmitir={() => acoesHeader.current.emitir()}
        onSalvar={() => acoesHeader.current.salvar()}
        onDescartar={() => acoesHeader.current.descartar()}
        onCancelarNfe={() => setShowCancelNfe(true)}
        onVerDanfe={linkDanfe ? () => window.open(linkDanfe, '_blank', 'noopener') : undefined}
      />,
    );
    return () => setHeaderActions?.(null);
  }, [setHeaderActions, statusNota, invoiceId, emissorIndisponivel, emitindo, salvando, alterado, linkDanfe]);

  const rotuloDaNota = rotuloNota(invoice?.serie, invoice?.numeroNF, invoiceId);

  return (
    <>
      <NotaGridContainer>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {rascunhoDiverge && (
            <AvisoPedidoAlterado>
              <TextoAviso>
                <span>Este rascunho está diferente do pedido.</span>
                {divergenciasPedido.map((d) => (
                  <small key={`${d.itemId}-${d.tipo}`}>{textoDivergencia(d)}</small>
                ))}
              </TextoAviso>
              <AvisoAcao type="button" onClick={() => setConfirmaRefazer(true)}>
                Refazer a partir do pedido
              </AvisoAcao>
            </AvisoPedidoAlterado>
          )}
          <DestinatarioSection
            companies={companies}
            selectedDestinatario={selectedDestinatario}
            onSelect={setSelectedDestinatario}
            readOnly={isEmitida}
            invoiceId={invoiceId ? Number(invoiceId) : undefined}
            onSaved={loadInvoice}
            orderClientId={invoice?.Order?.clientId ?? null}
          />
          <ItensSection
            items={itensExibidos}
            setItems={setItemsPeloUsuario}
            cfops={cfops}
            cfopsPerItem={cfopsPerItem}
            destStateId={destStateId}
            isEmitida={isEmitida}
            onRemove={setRemoveItemTarget}
            fetchNcmsForItem={fetchNcmsForItem}
            ncmOptions={ncmOptions}
            onAddItem={podeEditarItens && invoice?.orderId ? () => setShowAddItems(true) : undefined}
            semEmissor={!companyEmissorId}
            cenario={cenario}
          />
          <PagamentoSection
            invoice={invoice}
            invoiceId={Number(invoiceId)}
            readOnly={isEmitida}
            onSaved={loadInvoice}
          />
          <FreteSection
            invoice={invoice}
            invoiceId={Number(invoiceId)}
            companies={transportadoras}
            readOnly={isEmitida}
            onSaved={loadInvoice}
          />
          <InfoAdicionaisSection invoice={invoice} />
        </div>

        <ColunaLateral>
          {statusNota === 'NEGADA' && invoice?.motivoStatus && (
            <NegadaAlert>
              <NegadaIcone>⚠</NegadaIcone>
              <span>{invoice.motivoStatus}</span>
            </NegadaAlert>
          )}
          <IdentificacaoSection
            status={statusNota}
            emissoras={emissoras ?? []}
            emissorIndisponivel={emissorIndisponivel}
            invoice={invoice}
            value={identValues}
            onChange={(next) => {
              usuarioEditou.current = true;
              setIdentValues((prev) => ({ ...prev, ...next }));
            }}
            readOnly={isEmitida}
          />
          <TotaisSection items={itensExibidos} frete={valorFreteNota} />
        </ColunaLateral>
      </NotaGridContainer>

      {removeItemTarget && (
        <RemoveInvoiceItemModal
          item={removeItemTarget}
          onCancel={() => setRemoveItemTarget(null)}
          onConfirm={async (quantidade) => {
            try {
              await ApiService.fiscal.removeInvoiceItem(Number(invoiceId), removeItemTarget.id, quantidade);
            } catch (error: any) {
              setFeedback({ message: error?.response?.data?.message || 'Erro ao remover item.', isOpen: true, statusOk: false });
            } finally {
              // Sempre re-sincroniza a tela com o banco: se a remoção falhar, o item
              // reaparece — a tela nunca fica dessincronizada do que está persistido.
              setRemoveItemTarget(null);
              loadInvoice();
            }
          }}
        />
      )}

      {showAddItems && invoiceId && invoice?.orderId && (
        <AddInvoiceItemsModal
          invoiceId={Number(invoiceId)}
          orderId={Number(invoice.orderId)}
          onClose={() => setShowAddItems(false)}
          onAdded={() => {
            setShowAddItems(false);
            setFeedback({ message: 'Itens adicionados à nota.', isOpen: true, statusOk: true });
            loadInvoice();
          }}
          onError={(msg) => setFeedback({ message: msg, isOpen: true, statusOk: false })}
        />
      )}

      <ConfirmationModal
        isOpen={confirmaSaida}
        title="Descartar alterações?"
        message="As alterações não salvas desta nota serão perdidas."
        actions={[
          { label: 'Continuar editando', variant: 'outline', onClick: () => setConfirmaSaida(false) },
          {
            label: 'Descartar',
            variant: 'danger',
            onClick: () => {
              setConfirmaSaida(false);
              navigate(-1);
            },
          },
        ]}
      />

      <ConfirmationModal
        isOpen={confirmaRefazer}
        title="Refazer a partir do pedido?"
        message="Este rascunho será excluído e você volta à lista de pedidos para faturar o pedido de novo. As edições feitas neste rascunho serão perdidas."
        actions={[
          { label: 'Voltar', variant: 'outline', onClick: () => setConfirmaRefazer(false) },
          { label: refazendo ? 'Excluindo…' : 'Excluir e refazer', variant: 'danger', onClick: handleRefazer },
        ]}
      />

      <ConfirmationModal
        isOpen={showCancelNfe}
        title="Cancelar NF-e"
        message={`Tem certeza que deseja cancelar a nota ${rotuloDaNota}? O cancelamento é irreversível e só é permitido em até 24h da autorização.`}
        actions={[
          { label: 'Voltar', onClick: () => setShowCancelNfe(false), variant: 'outline' },
          {
            label: cancelandoNfe ? 'Cancelando…' : 'Cancelar NF-e',
            onClick: confirmarCancelamentoNfe,
            variant: 'danger',
          },
        ]}
      />
    </>
  );
};

export default InvoiceForm;
